import { Fragment, useState, useTransition } from 'react';
import { toast } from 'sonner';
import { MonitorCheck, Check, ClipboardX } from 'lucide-react';
import { useMutation } from '@apollo/client/react';

import { apolloCache as cache, apolloClient as client } from 'src/graphql';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTrigger,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useSaasEscrow } from 'src/context/saas-escrow';
import {
  AssetContentTypeEnum,
  AssetsOrderByEnum,
  ContentTypeEnum,
  SaasCredentialsOrderByEnum,
} from 'src/graphql/schema';
import { AssetsDocument } from 'src/graphql/actions/asset/assets.query';
import {
  SaasCredentialsDocument,
  ISaasCredentialsQueryVariables,
  ISaasCredentialsQuery,
} from 'src/graphql/actions/saasCredentials/saasCredentials.query';
import { SoftwareEscrowActivateDocument } from 'src/graphql/actions/softwareEscrow/softwareEscrowActivate.mutation';
import { Checkbox } from '@/components/ui/checkbox';
import { getApiError, ClientError } from 'src/utils/errors';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { refetchAppProtections } from 'src/containers/Applications/Protections/api';

import type { IAssetsQuery, IAssetsQueryVariables } from 'src/graphql/actions/asset/assets.query';

const refetchAssets = () => {
  client.cache.evict({ fieldName: 'assets' });
  client.cache.gc();
  client.refetchQueries({
    include: [AssetsDocument],
  });
};

const ApproveSignatureTooltip = () => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger asChild>
        <div>
          <Button variant="secondary" disabled className="w-full" size="sm">
            <MonitorCheck /> Activate
          </Button>
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        className="bg-popover text-popover-foreground max-w-[320px] border border-border text-sm"
      >
        You need to add at least one asset to get your escrow activated.
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const AssetSummaryValue = ({ isMarkedAsRequired = false, isEmpty = false }) => {
  if (!isMarkedAsRequired) {
    return (
      <div className="w-15 flex justify-center">
        <div className="flex items-center justify-center size-4 bg-gray-200 border border-gray-500 rounded-full shadow-sm">
          <ClipboardX className="size-2.5 text-gray-500" />
        </div>
      </div>
    );
  }

  if (isEmpty) return <div className="text-sm text-gray-400">0 assets</div>;

  return (
    <div className="w-15 flex justify-center">
      <div className="flex items-center justify-center size-4 bg-green-100 border border-green-500 rounded-full shadow-sm">
        <Check className="size-2.5 text-green-800" />
      </div>
    </div>
  );
};

const AssetSummaryItem = ({
  label,
  isMarkedAsRequired,
  isEmpty,
}: {
  label: string;
  isMarkedAsRequired: boolean;
  isEmpty: boolean;
}) => {
  return (
    <div className="flex items-center justify-between gap-2 py-2">
      <div className="text-sm font-medium text-foreground">{label}</div>
      <div className="flex items-center gap-3">
        {!isMarkedAsRequired && <div className="text-xs text-gray-400">(Marked as not required)</div>}
        <AssetSummaryValue isMarkedAsRequired={isMarkedAsRequired} isEmpty={isEmpty} />
      </div>
    </div>
  );
};

const AssetsSummary = () => {
  const {
    saasEscrow: { id: saasEscrowId, requiredContentTypes },
  } = useSaasEscrow();
  const sourceCodeAssets = cache.readQuery<IAssetsQuery, IAssetsQueryVariables>({
    query: AssetsDocument,
    variables: {
      softwareEscrowId: saasEscrowId,
      contentType: AssetContentTypeEnum.SourceCode,
      perPage: 1,
      orderBy: AssetsOrderByEnum.LatestDeposit,
    },
  });
  const documentationAssets = cache.readQuery<IAssetsQuery, IAssetsQueryVariables>({
    query: AssetsDocument,
    variables: {
      softwareEscrowId: saasEscrowId,
      contentType: AssetContentTypeEnum.Documentation,
      perPage: 1,
      orderBy: AssetsOrderByEnum.LatestDeposit,
    },
  });
  const dataAssets = cache.readQuery<IAssetsQuery, IAssetsQueryVariables>({
    query: AssetsDocument,
    variables: {
      softwareEscrowId: saasEscrowId,
      contentType: AssetContentTypeEnum.Data,
      perPage: 1,
      orderBy: AssetsOrderByEnum.LatestDeposit,
    },
  });
  const deploymentAssets = cache.readQuery<IAssetsQuery, IAssetsQueryVariables>({
    query: AssetsDocument,
    variables: {
      softwareEscrowId: saasEscrowId,
      contentType: AssetContentTypeEnum.Deployment,
      perPage: 1,
      orderBy: AssetsOrderByEnum.LatestDeposit,
    },
  });
  const thirdPartyDependencyAssets = cache.readQuery<IAssetsQuery, IAssetsQueryVariables>({
    query: AssetsDocument,
    variables: {
      softwareEscrowId: saasEscrowId,
      contentType: AssetContentTypeEnum.ThirdPartyDependency,
      perPage: 1,
      orderBy: AssetsOrderByEnum.LatestDeposit,
    },
  });
  const credentialsAssets = cache.readQuery<ISaasCredentialsQuery, ISaasCredentialsQueryVariables>({
    query: SaasCredentialsDocument,
    variables: {
      saasEscrowId: saasEscrowId,
      perPage: 1,
      orderBy: SaasCredentialsOrderByEnum.UpdatedAt,
    },
  });

  const assetsInfo = [
    {
      id: ContentTypeEnum.SourceCode,
      label: 'Source code',
      isEmpty: !sourceCodeAssets?.assets.nodesCount,
    },
    {
      id: ContentTypeEnum.Documentation,
      label: 'Documentation',
      isEmpty: !documentationAssets?.assets.nodesCount,
    },
    {
      id: ContentTypeEnum.Data,
      label: 'Data',
      isEmpty: !dataAssets?.assets.nodesCount,
    },
    {
      id: ContentTypeEnum.Deployment,
      label: 'Deployment infrastructure & artifacts',
      isEmpty: !deploymentAssets?.assets.nodesCount,
    },
    {
      id: ContentTypeEnum.ThirdPartyDependency,
      label: '3rd party dependencies & services',
      isEmpty: !thirdPartyDependencyAssets?.assets.nodesCount,
    },
    {
      id: ContentTypeEnum.Credentials,
      label: 'Secrets, credentials & access',
      isEmpty: !credentialsAssets?.saasCredentials.nodesCount,
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between gap-3 py-2 text-xs text-gray-500">
        <strong>Asset type</strong>
        <strong>In Escrow</strong>
      </div>
      {assetsInfo.map((asset) => (
        <AssetSummaryItem
          key={asset.id}
          label={asset.label}
          isMarkedAsRequired={requiredContentTypes.includes(asset.id)}
          isEmpty={asset.isEmpty}
        />
      ))}
    </div>
  );
};

const handleActivated = () => {
  refetchAssets();
  refetchAppProtections();
};

const ActivateEscrowDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const {
    saasEscrow: { id: saasEscrowId, policy },
    refetch,
  } = useSaasEscrow();
  const [activateEscrow, { loading }] = useMutation(SoftwareEscrowActivateDocument, {
    onCompleted: handleActivated,
  });

  const onActivateEscrow = async () => {
    try {
      const { data } = await activateEscrow({
        variables: {
          softwareEscrowId: saasEscrowId,
        },
      });

      if (data?.softwareEscrowActivate?.success) {
        setIsOpen(false);
        setIsSuccessOpen(true);
      } else {
        throw new ClientError(data?.softwareEscrowActivate?.errors?.[0]);
      }
    } catch (error: unknown) {
      getApiError(error, toast.error);
    }
  };

  const onBackToDashboard = () =>
    startTransition(async () => {
      await refetch();
      setIsSuccessOpen(false);
    });

  const onSuccessOpenChange = (open: boolean) => {
    if (!open) onBackToDashboard();
  };

  const onCheckedChange = (checked: boolean) => setIsConfirmed(checked);

  if (!policy.deferActivation && !policy.activate) return <ApproveSignatureTooltip />;

  return (
    <Fragment>
      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogTrigger asChild>
          <Button variant="secondary" size="sm">
            <MonitorCheck />
            Activate
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <h2 className="text-2xl font-semibold text-foreground">Almost there!</h2>
          <Separator />
          <p className="text-lg font-semibold text-foreground">Please check that the following is correct:</p>
          <AssetsSummary />
          <Separator />
          <AlertDialogHeader className="gap-4">
            <AlertDialogTitle>Are you sure you want to activate your escrow?</AlertDialogTitle>
            <AlertDialogDescription>
              By clicking Activate Escrow, you confirm that all assets required to recover this application have been
              added to this escrow.
            </AlertDialogDescription>
            <div className="flex items-start gap-2">
              <Checkbox
                id="activate-escrow"
                checked={isConfirmed}
                onCheckedChange={onCheckedChange}
                className="mt-0.5"
              />
              <Label htmlFor="activate-escrow" className="font-medium text-sm mb-0">
                I confirm that I have added all assets required for application recovery to this escrow.
              </Label>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline">Cancel</Button>
            </AlertDialogCancel>
            <Button onClick={onActivateEscrow} disabled={!isConfirmed || loading} isLoading={loading}>
              Activate Escrow
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isSuccessOpen} onOpenChange={onSuccessOpenChange}>
        <DialogContent className="sm:max-w-[441px]">
          <DialogHeader className="text-center">
            <DialogTitle className="text-lg">Escrow activation in progress!</DialogTitle>
            <DialogDescription>
              Your assets have now successfully been connected to this escrow! Please note that larger deposits may take
              a moment to sync.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button className="w-full" disabled={isPending} isLoading={isPending} onClick={onBackToDashboard}>
              Back to escrow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

export default ActivateEscrowDialog;
